import { Box } from "@mui/material";
import { memo, useEffect, useRef, useState } from "react";
import { Page } from "react-pdf";

import type { DocumentPreviewHighlight } from "./DocumentPreview";

import { RegionOutline } from "./RegionOutline";

/** How far outside the viewport a page starts drawing, so scrolling rarely meets a blank page. */
const PRELOAD_MARGIN = "100% 0px";

interface PdfPageProps {
  pageIndex: number;
  /** The page at scale 1. */
  size: { width: number; height: number };
  zoom: number;
  /** What clips this page as the reader scrolls, or null for the window. */
  scrollRoot: HTMLElement | null;
  highlights: readonly DocumentPreviewHighlight[];
  frameKey: string | null;
  attachFramedOutline: (element: HTMLDivElement | null) => void;
  customTextRenderer?: (item: { str: string; itemIndex: number; pageIndex: number }) => string;
}

/** One page, laid out at its true size before it draws. */
export const PdfPage = memo(function PdfPage({
  pageIndex,
  size,
  zoom,
  scrollRoot,
  highlights,
  frameKey,
  attachFramedOutline,
  customTextRenderer,
}: PdfPageProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (wrapper == null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const latest = entries.at(-1);
        if (latest != null) setNear(latest.isIntersecting);
      },
      { root: scrollRoot, rootMargin: PRELOAD_MARGIN }
    );
    // State is set from the observer's callback, not from the props this effect reads.
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [scrollRoot]);

  const onThisPage = highlights.filter((h) => h.page - 1 === pageIndex);
  return (
    <Box
      ref={wrapperRef}
      data-testid="pdf-page"
      data-page-index={pageIndex}
      sx={{
        position: "relative",
        // Explicit px: sx reads a number of 1 or less as a fraction.
        width: `${size.width * zoom}px`,
        height: `${size.height * zoom}px`,
        mx: "auto",
        my: 1,
        bgcolor: "common.white",
        boxShadow: 1,
      }}
    >
      {/* Only near the viewport, so pages scrolled past release their canvas. */}
      {near && (
        <Page
          pageIndex={pageIndex}
          scale={zoom}
          customTextRenderer={customTextRenderer}
          loading={null}
        />
      )}
      {/* Always present, so a framed region can be scrolled to before its canvas renders. */}
      {onThisPage.map((h) => {
        const key = `${h.page}:${h.region.x}:${h.region.y}:${h.region.width}:${h.region.height}`;
        return (
          <RegionOutline
            key={`${key}:${h.label ?? ""}`}
            region={h.region}
            label={h.label}
            outlineRef={frameKey != null && key === frameKey ? attachFramedOutline : undefined}
          />
        );
      })}
    </Box>
  );
});
